/**
 * Mirrors Emeis.Licensing.Service.Contracts on the backend. Enum values are
 * the integers the API sends; the label helpers live in
 * src/utils/licensing.ts.
 */

export const MeterKind = { Capacity: 1, Allowance: 2 } as const;
export const MeterLimitType = { Hard: 1, Soft: 2 } as const;
export const MeterResetPeriod = { Never: 0, BillingPeriod: 1, Monthly: 2 } as const;
export const FeatureValueType = { Boolean: 1, Level: 2 } as const;
export const PlanType = { Trial: 1, Standard: 2, Enterprise: 3 } as const;
export const BillingCycle = { Monthly: 1, Yearly: 2, Trial: 3, Custom: 4 } as const;
export const SubscriptionStatus = { Trial: 1, Active: 2, PastDue: 3, Suspended: 4, Cancelled: 5, Expired: 6 } as const;
export const OverrideTargetType = { Meter: 1, Feature: 2, Module: 3 } as const;
export const RechargePackType = { Meter: 1, Module: 2 } as const;
export const RechargePriceType = { OneTime: 1, MonthlyRecurring: 2 } as const;
export const RechargeValidityType = { UntilSubscriptionExpiry: 1, FixedDays: 2, NoExpiry: 3 } as const;
export const RechargeStatus = { Pending: 1, Active: 2, Exhausted: 3, Expired: 4, Cancelled: 5 } as const;
export const RechargeSource = { Purchase: 1, ManualGrant: 2, Promotional: 3, Overage: 4 } as const;
export const EnforcementMode = { Monitor: 1, Enforce: 2 } as const;
export const UnsubscribedOrganisationPolicy = { Unrestricted: 1, DefaultPlan: 2 } as const;

export interface MeterDto {
  id?: string;
  key: string;
  name: string;
  description?: string;
  category?: string;
  unit?: string;
  kind: number;
  limitType: number;
  resetPeriod: number;
  isRechargeable: boolean;
  showInUsageDashboard: boolean;
  isActive: boolean;
  sortOrder: number;
}

export interface FeatureDto {
  id?: string;
  key: string;
  name: string;
  description?: string;
  category?: string;
  valueType: number;
  levelOptions?: string;
  isActive: boolean;
  sortOrder: number;
}

export interface ModuleDto {
  id?: string;
  key: string;
  name: string;
  description?: string;
  organisationModule?: number | null;
  isCore: boolean;
  levelOptions?: string;
  isActive: boolean;
  sortOrder: number;
}

export interface AiOperationDto {
  id?: string;
  key: string;
  name: string;
  description?: string;
  credits: number;
  requiredFeatureKey?: string;
  isActive: boolean;
  sortOrder: number;
}

export interface CatalogueResponse {
  meters: MeterDto[];
  features: FeatureDto[];
  modules: ModuleDto[];
  aiOperations: AiOperationDto[];
}

export interface RechargePackDto {
  id?: string;
  /** Active or pending recharges bought from this pack; a pack in use cannot be removed. */
  inUseCount?: number;
  code?: string;
  name: string;
  description?: string;
  packType: number;
  meterKey?: string;
  meterName?: string;
  moduleKey?: string;
  moduleName?: string;
  quantity: number;
  unit?: string;
  price: number;
  currency?: string;
  priceType: number;
  validityType: number;
  validityDays?: number | null;
  annualDiscountPercent?: number | null;
  tierLabel?: string;
  isActive: boolean;
  sortOrder: number;
  effectiveUnitPrice?: number | null;
}

export interface LicensingSettingsDto {
  enforcementMode: number;
  unsubscribedOrganisationPolicy: number;
  defaultPlanId?: string | null;
  autoProvisionTrialForNewOrganisations: boolean;
  trialPlanId?: string | null;
  alertThresholds: string;
  upgradeRecommendationThresholdPercent: number;
  upgradeRecommendationConsecutivePeriods: number;
  autoActivateRecharges: boolean;
  annualSavingsLabel?: string;
  currency: string;
  gracePeriodDays: number;
  entitlementCacheSeconds: number;
  allowSoftLimitOverage: boolean;
  notifyOrganisationAdminsOnAlerts: boolean;
  sellerName?: string;
  sellerAddress?: string;
  sellerEmail?: string;
  sellerPhone?: string;
  sellerWebsite?: string;
  sellerTaxNumber?: string;
  taxPercent: number;
  invoiceDueDays: number;
  invoicePrefix?: string;
  invoiceFooterNote?: string;
  /** Invoice logo as a data URL; empty string clears it, undefined leaves it unchanged. */
  sellerLogoDataUrl?: string | null;
  sellerCin?: string;
  sellerPan?: string;
  sellerState?: string;
  /** 1 = CGST + SGST (intra-state), 2 = IGST (inter-state). */
  taxMode: number;
  cgstPercent: number;
  sgstPercent: number;
  igstPercent: number;
  bankName?: string;
  bankAccountName?: string;
  bankAccountNumber?: string;
  bankIfsc?: string;
  bankBranch?: string;
  upiId?: string;
  paymentInstructions?: string;
}

export const TaxMode = { CgstSgst: 1, Igst: 2 } as const;

export interface PlanEntitlementDto {
  meterKey: string;
  meterName?: string;
  unit?: string;
  kind?: number;
  includedQuantity?: number | null;
  isUnlimited: boolean;
  limitTypeOverride?: number | null;
  overageUnitPrice?: number | null;
  displayNote?: string | null;
}

export interface PlanFeatureDto {
  featureKey: string;
  featureName?: string;
  valueType?: number;
  enabled: boolean;
  level?: string | null;
}

export interface PlanModuleDto {
  moduleKey: string;
  moduleName?: string;
  isCore?: boolean;
  included: boolean;
  accessLevel?: string | null;
  addOnAvailable: boolean;
  addOnMonthlyPrice?: number | null;
  addOnYearlyPrice?: number | null;
}

export interface PlanDto {
  id?: string;
  code?: string;
  name: string;
  tagline?: string;
  description?: string;
  planType: number;
  tier: number;
  trialDays: number;
  monthlyPrice: number;
  yearlyPrice: number;
  currency?: string;
  isCustomPricing: boolean;
  priceLabel?: string;
  isPublished: boolean;
  isActive: boolean;
  isMostPopular: boolean;
  annualPoolEnabled: boolean;
  targetAudience?: string;
  highlights?: string;
  sortOrder: number;
  activeSubscriptions?: number;
  effectiveMonthlyPriceOnYearly?: number | null;
  annualSavingPercent?: number | null;
  entitlements: PlanEntitlementDto[];
  features: PlanFeatureDto[];
  modules: PlanModuleDto[];
}

export interface SubscriptionOverrideDto {
  targetType: number;
  key: string;
  name?: string;
  quantity?: number | null;
  isUnlimited?: boolean | null;
  enabled?: boolean | null;
  level?: string | null;
  limitTypeOverride?: number | null;
  note?: string;
}

export interface SubscriptionDto {
  id: string;
  organisationId: string;
  planId: string;
  planCode?: string;
  planName?: string;
  planTier: number;
  planType: number;
  billingCycle: number;
  status: number;
  startDate: string;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  trialEndsAt?: string | null;
  autoRenew: boolean;
  negotiatedPrice?: number | null;
  discountPercent: number;
  discountNote?: string | null;
  effectivePrice?: number | null;
  currency?: string;
  notes?: string;
  isCurrent: boolean;
  isVirtual: boolean;
  endedAt?: string | null;
  endReason?: string | null;
  requestedPlanId?: string | null;
  requestedPlanName?: string | null;
  requestedBillingCycle?: number | null;
  planChangeRequestedAt?: string | null;
  planChangeRequestNote?: string | null;
  overrides: SubscriptionOverrideDto[];
  modificationDate?: string;
}

export interface RechargeDto {
  id: string;
  organisationId: string;
  organisationName?: string;
  packId?: string | null;
  packName?: string;
  packType: number;
  meterKey?: string | null;
  meterName?: string | null;
  unit?: string | null;
  moduleKey?: string | null;
  moduleName?: string | null;
  quantity: number;
  remainingQuantity: number;
  price: number;
  currency?: string;
  status: number;
  source: number;
  purchasedAt: string;
  activatedAt?: string | null;
  expiresAt?: string | null;
  isRecurring: boolean;
  paymentReference?: string | null;
  notes?: string | null;
}

export interface UsageAlertDto {
  id: string;
  meterKey: string;
  meterName: string;
  periodKey: string;
  thresholdPercent: number;
  usedAtTrigger: number;
  includedAtTrigger: number;
  triggeredAt: string;
  acknowledgedAt?: string | null;
}

export interface LedgerRowDto {
  id: string;
  moduleKey?: string | null;
  meterKey: string;
  meterName: string;
  action?: string | null;
  quantity: number;
  unit?: string | null;
  userId?: string | null;
  userName?: string | null;
  referenceId?: string | null;
  referenceType?: string | null;
  referenceName?: string | null;
  source: number;
  creditsConsumed: number;
  includedConsumed: number;
  rechargeConsumed: number;
  overageQuantity: number;
  periodKey?: string;
  occurredAt: string;
  wouldHaveBeenBlocked: boolean;
  notes?: string | null;
}

export interface MeterUsage {
  key: string;
  name: string;
  category?: string;
  unit?: string;
  kind: number;
  limitType: number;
  resetPeriod: number;
  isRechargeable: boolean;
  sortOrder: number;
  used: number;
  included?: number | null;
  isUnlimited: boolean;
  rechargeAvailable: number;
  remaining?: number | null;
  percentUsed?: number | null;
  overage: number;
  periodKey?: string;
  periodStart?: string | null;
  periodEnd?: string | null;
  highestAlertThreshold: number;
  overageUnitPrice?: number | null;
  displayNote?: string | null;
}

export interface ModuleEntitlement {
  key: string;
  name: string;
  organisationModule?: number | null;
  isCore: boolean;
  included: boolean;
  accessLevel?: string | null;
  addOnAvailable: boolean;
  addOnMonthlyPrice?: number | null;
  addOnYearlyPrice?: number | null;
  viaAddOn: boolean;
  addOnExpiresAt?: string | null;
}

export interface FeatureEntitlement {
  key: string;
  name: string;
  category?: string;
  valueType: number;
  enabled: boolean;
  level?: string | null;
  levelOptions?: string | null;
}

export interface UpgradeRecommendationDto {
  recommended: boolean;
  nextPlanId?: string | null;
  nextPlanName?: string | null;
  nextPlanMonthlyPrice?: number | null;
  recentRechargeSpend: number;
  spendAsPercentOfNextPlan?: number | null;
  periodsConsidered: number;
  reason?: string | null;
}

export interface MySubscriptionResponse {
  organisationId: string;
  organisationName?: string;
  isUnrestricted: boolean;
  isBlocked: boolean;
  blockedReason?: string | null;
  enforcementMode: number;
  subscription?: SubscriptionDto | null;
  plan?: PlanDto | null;
  modules: ModuleEntitlement[];
  features: FeatureEntitlement[];
  usage: MeterUsage[];
  alerts: UsageAlertDto[];
  recharges: RechargeDto[];
  upgradeRecommendation?: UpgradeRecommendationDto | null;
  annualSavingsLabel?: string;
  currency?: string;
  autoActivateRecharges: boolean;
  alertThresholds?: number[];
}

export interface EntitlementSnapshotResponse {
  organisationId: string;
  isUnrestricted: boolean;
  isBlocked: boolean;
  blockedReason?: string | null;
  enforce: boolean;
  planCode?: string | null;
  planName?: string | null;
  planTier?: number | null;
  status?: number | null;
  currentPeriodEnd?: string | null;
  trialEndsAt?: string | null;
  modules: Record<string, boolean>;
  moduleLevels: Record<string, string>;
  features: Record<string, boolean>;
  featureLevels: Record<string, string>;
  usage: MeterUsage[];
  highestAlert: number;
  unacknowledgedAlerts: UsageAlertDto[];
}

export interface EntitlementCheckResult {
  allowed: boolean;
  isUnrestricted: boolean;
  isOverage: boolean;
  wouldBeBlockedInEnforceMode: boolean;
  denyCode?: string | null;
  message?: string | null;
  meterKey?: string;
  meterName?: string;
  unit?: string;
  kind: number;
  limitType: number;
  used: number;
  included?: number | null;
  isUnlimited: boolean;
  rechargeAvailable: number;
  requested: number;
  remaining?: number | null;
  percentUsed?: number | null;
  isRechargeable: boolean;
  periodKey?: string;
  periodEnd?: string | null;
}

export interface OrganisationLicenceRowDto {
  organisationId: string;
  organisationName: string;
  organisationCode?: string;
  organisationEmail?: string;
  organisationIsActive: boolean;
  subscription?: SubscriptionDto | null;
  pendingRecharges: number;
  hasPlanChangeRequest: boolean;
}

export interface OrganisationLicenceDetailResponse {
  organisation: OrganisationLicenceRowDto;
  history: SubscriptionDto[];
  recharges: RechargeDto[];
  usage: MeterUsage[];
  alerts: UsageAlertDto[];
  modules: ModuleEntitlement[];
  features: FeatureEntitlement[];
  isUnrestricted: boolean;
}

export interface DailyUsagePoint {
  meterKey: string;
  day: string;
  quantity: number;
}

/** Body of an HTTP 402 from any service, raised by the entitlement engine. */
export interface EntitlementErrorResponse {
  status: number;
  message: string;
  errors?: string[];
  code: string;
  meterKey?: string;
  meterName?: string;
  unit?: string;
  used: number;
  included?: number | null;
  rechargeAvailable: number;
  requested: number;
  isRechargeable: boolean;
  suggestedActions: string[];
}

/** Stable keys shared with the backend (Emeis.Shared.Licensing.LicensingKeys). */
export const LicensingModuleKeys = {
  ContractStudio: "contract_studio",
  ContractRepository: "contract_repository",
  ObligationHub: "obligation_hub",
  ReceivablesEngine: "receivables_engine",
  Workflow: "workflow",
  ESign: "esign",
  Ai: "ai",
  Analytics: "analytics",
  Api: "api",
  Integrations: "integrations",
} as const;

export const LicensingMeterKeys = {
  Users: "users",
  AdminUsers: "admin_users",
  ActiveContracts: "active_contracts",
  LegacyContracts: "legacy_contracts",
  Templates: "templates",
  AiCredits: "ai_credits",
  BulkDocuments: "bulk_documents",
  StorageGb: "storage_gb",
  EsignEnvelopes: "esign_envelopes",
  ObligationRecords: "obligation_records",
  ReceivableRecords: "receivable_records",
  Workflows: "workflows",
  ApiCalls: "api_calls",
  Integrations: "integrations",
} as const;

// ---- Billing ---------------------------------------------------------------

export const InvoiceType = { Subscription: 1, Recharge: 2, Adjustment: 3 } as const;
export const InvoiceStatus = { Draft: 1, Issued: 2, Paid: 3, Overdue: 4, Cancelled: 5 } as const;
export const PaymentMethod = { Offline: 1, BankTransfer: 2, Upi: 3, Card: 4, Gateway: 5, Credit: 6 } as const;
export const PaymentStatus = { Succeeded: 1, Pending: 2, Failed: 3, Refunded: 4 } as const;

export interface InvoiceLineDto {
  description: string;
  quantity: number;
  unit?: string | null;
  unitPrice: number;
  amount: number;
}

export interface PaymentDto {
  id: string;
  organisationId: string;
  organisationName?: string | null;
  invoiceId?: string | null;
  invoiceNumber?: string | null;
  amount: number;
  currency?: string;
  method: number;
  status: number;
  reference?: string | null;
  paidAt: string;
  notes?: string | null;
}

export interface InvoiceDto {
  id: string;
  invoiceNumber: string;
  organisationId: string;
  organisationName?: string | null;
  subscriptionId?: string | null;
  rechargeId?: string | null;
  type: number;
  status: number;
  issueDate: string;
  dueDate: string;
  periodStart?: string | null;
  periodEnd?: string | null;
  currency: string;
  subtotal: number;
  discountPercent: number;
  discountAmount: number;
  discountNote?: string | null;
  taxableAmount: number;
  taxPercent: number;
  taxAmount: number;
  taxMode: number;
  cgstPercent: number;
  cgstAmount: number;
  sgstPercent: number;
  sgstAmount: number;
  igstPercent: number;
  igstAmount: number;
  total: number;
  totalInWords?: string | null;
  amountPaid: number;
  amountDue: number;
  paidAt?: string | null;
  title?: string | null;
  billToName?: string | null;
  billToAddress?: string | null;
  billToEmail?: string | null;
  billToTaxNumber?: string | null;
  billToCin?: string | null;
  billToContactName?: string | null;
  billToPhone?: string | null;
  sellerName?: string | null;
  sellerAddress?: string | null;
  sellerEmail?: string | null;
  sellerPhone?: string | null;
  sellerWebsite?: string | null;
  sellerTaxNumber?: string | null;
  sellerCin?: string | null;
  sellerPan?: string | null;
  sellerState?: string | null;
  sellerLogoDataUrl?: string | null;
  bankName?: string | null;
  bankAccountName?: string | null;
  bankAccountNumber?: string | null;
  bankIfsc?: string | null;
  bankBranch?: string | null;
  upiId?: string | null;
  paymentInstructions?: string | null;
  lines: InvoiceLineDto[];
  notes?: string | null;
  emailedAt?: string | null;
  emailedTo?: string | null;
  payments?: PaymentDto[];
}

export interface BillingSummaryDto {
  totalBilled: number;
  totalPaid: number;
  outstanding: number;
  openInvoices: number;
  overdueInvoices: number;
  currency?: string;
}

export interface AuditLogDto {
  id: string;
  occurredAt: string;
  actorUserId: string;
  actorName?: string | null;
  actorIsAppAdmin: boolean;
  action: string;
  entityType: string;
  entityId?: string | null;
  entityName?: string | null;
  organisationId?: string | null;
  organisationName?: string | null;
  reason?: string | null;
  beforeJson?: string | null;
  afterJson?: string | null;
  isDeletion: boolean;
  /** JSON array of { field, from, to } for edits. */
  changesJson?: string | null;
}

export interface AuditSearchMessage {
  organisationId?: string | null;
  entityType?: string | null;
  entityId?: string | null;
  action?: string | null;
  deletionsOnly?: boolean | null;
  searchText?: string | null;
  from?: string | null;
  to?: string | null;
  pageIndex: number;
  pageSize: number;
}
